/** biome-ignore-all lint/style/noMagicNumbers: <explanation> */

import { DndContext } from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { DEFAULT_WORKSPACE_SCHEDULE_SETTINGS } from "@elaso/shared/types/db";
import { RoomAudioRenderer, RoomContext } from "@livekit/components-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { type GridRoutes, gridRoutes } from "@/grid-routes";
import { useVoiceStageActiveAsset } from "@/hooks/use-voice-stage-active-asset";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { livekitUrlFor } from "@/lib/connection-target";
import { getParticipantDisplayName } from "@/lib/livekit-utils";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";
import "@livekit/components-styles";
import { captureException } from "@sentry/react";
import { ConnectionError, Room } from "livekit-client";
import {
	CalendarDays,
	FileText,
	Hash,
	MessageSquare,
	Radio,
	Video,
	X,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { buttonVariants } from "../ui/button";
import { Card } from "../ui/card";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "../ui/sidebar";
import { AppTour } from "./app-tour";
import { FloatingPipPanel } from "./floating-pip-panel";
import { RightSidebar } from "./right-sidebar";
import { Sidebar } from "./sidebar";

export function Grid() {
	const { isConnected } = useWsConnected();
	// Lives here rather than in the stage view, because this component outlives
	// every tab switch, so the subscription spans the whole voice session (see
	// the hook).
	useVoiceStageActiveAsset();
	const gridItems = useStore(uiStore, (state) => state.gridItems);
	const setActiveTab = useStore(uiStore, (state) => state.setActiveTab);
	const closeTab = useStore(uiStore, (state) => state.closeTab);
	const channels = useStore(coreStore, (state) => state.channels);
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const room = useStore(uiStore, (state) => state.room);
	const setRoom = useStore(uiStore, (state) => state.setRoom);
	const handleLeaveChannel = useStore(
		uiStore,
		(state) => state.handleLeaveChannel
	);
	const activeBroadcastViewer = useStore(
		uiStore,
		(state) => state.activeBroadcastViewer
	);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	const setBroadcastRoom = useStore(uiStore, (state) => state.setBroadcastRoom);
	const handleLeaveBroadcast = useStore(
		uiStore,
		(state) => state.handleLeaveBroadcast
	);
	const setIsBroadcastAudioActive = useStore(
		uiStore,
		(state) => state.setIsBroadcastAudioActive
	);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const documents = useStore(coreStore, (state) => state.documents);
	const workspaces = useStore(coreStore, (state) => state.workspaces);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const currentWorkspace = workspaces.get(currentWorkspaceId || "");
	const scheduleSettings =
		(
			currentWorkspace as {
				scheduleSettings?: { scheduleTitle?: string | null };
			}
		)?.scheduleSettings ?? DEFAULT_WORKSPACE_SCHEDULE_SETTINGS;
	const handleJoinVoiceChannel = useStore(
		uiStore,
		(state) => state.handleJoinVoiceChannel
	);
	const refreshActiveChannelToken = useStore(
		uiStore,
		(state) => state.refreshActiveChannelToken
	);
	const refreshActiveBroadcastViewerToken = useStore(
		uiStore,
		(state) => state.refreshActiveBroadcastViewerToken
	);
	const channelTokenRefreshAttemptedRef = useRef<string | null>(null);
	const broadcastTokenRefreshAttemptedRef = useRef(false);

	useEffect(() => {
		if (!isConnected) return;

		const pendingVoiceChannel = sessionStorage.getItem("pendingVoiceChannel");
		if (pendingVoiceChannel) {
			sessionStorage.removeItem("pendingVoiceChannel");
			try {
				const channel = JSON.parse(pendingVoiceChannel);
				if (channel.workspaceId && channel.workspaceId !== currentWorkspaceId) {
					// selectWorkspace sets the id and runs the sequential workspace-data
					// load. The sidebar fan-out effect that used to react to this id
					// change was removed in favour of the orchestrator.
					coreStore.getState().selectWorkspace(channel.workspaceId);
					sessionStorage.setItem(
						"pendingVoiceChannel",
						JSON.stringify(channel)
					);
					return;
				}
				handleJoinVoiceChannel(channel);
			} catch (error) {
				console.error("Failed to parse pending voice channel:", error);
			}
		}
	}, [isConnected, handleJoinVoiceChannel, currentWorkspaceId]);

	useEffect(() => {
		if (!activeChannel?.token) return;

		let mounted = true;
		let currentRoom: Room | null = null;
		const abortController = new AbortController();

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Room connection logic is complex but necessary
		const connectToRoom = async () => {
			try {
				const newRoom = new Room({
					adaptiveStream: true,
					dynacast: true,
				});

				currentRoom = newRoom;

				if (abortController.signal.aborted) {
					newRoom.disconnect();
					return;
				}

				newRoom.on("participantConnected", (participant) => {
					const displayName = getParticipantDisplayName(participant);
					console.log("Participant connected:", displayName);
				});

				newRoom.on("participantDisconnected", (participant) => {
					const displayName = getParticipantDisplayName(participant);
					console.log("Participant disconnected:", displayName);
				});

				newRoom.on("disconnected", () => {
					console.log("Room disconnected");
				});

				// The room's deployment when the Core named one (TECH-3525).
				const livekitUrl = livekitUrlFor(activeChannel.livekitUrl);
				console.log("Connecting to LiveKit:", livekitUrl);
				await newRoom.connect(livekitUrl, activeChannel.token);

				if (abortController.signal.aborted || !mounted) {
					console.log(
						"Abort connection attempt due to user initiated disconnect"
					);
					newRoom.disconnect();
					return;
				}

				console.log("Connected to room:", activeChannel.name);

				channelTokenRefreshAttemptedRef.current = null;
				setRoom(newRoom);
			} catch (error) {
				console.error(error);
				if (abortController.signal.aborted) {
					console.log("Connection aborted");
					return;
				}
				const isExpiredToken =
					error instanceof Error &&
					/token is expired|expired token/i.test(error.message);
				if (
					isExpiredToken &&
					mounted &&
					channelTokenRefreshAttemptedRef.current !== activeChannel.id
				) {
					channelTokenRefreshAttemptedRef.current = activeChannel.id;
					try {
						await refreshActiveChannelToken();
						return;
					} catch (refreshError) {
						if (SENTRY_ENABLED)
							captureException(refreshError, {
								tags: { context: "grid_room_token_refresh" },
							});
					}
				}
				if (SENTRY_ENABLED)
					captureException(error, { tags: { context: "grid_room_connect" } });
				console.error("Failed to connect to room:", error);
				if (mounted) {
					// A joiner that raced past the token-time capacity check is refused
					// by the SFU at connect (room-level maxParticipants, TECH-3128).
					// LiveKit surfaces that as a raw signal failure (HTTP 5xx), not a
					// typed "room full" error, so match the shape and word the toast
					// for the likely cause.
					const isLikelyFull =
						error instanceof ConnectionError && (error.status ?? 0) >= 500;
					toast.error(
						isLikelyFull
							? "Could not join — the channel may be full. Please try again in a moment."
							: "Failed to connect to voice channel. Please try again."
					);
					handleLeaveChannel();
				}
			}
		};

		connectToRoom();

		return () => {
			mounted = false;
			abortController.abort();

			if (currentRoom && !activeChannel) {
				currentRoom.disconnect();
			}
		};
	}, [
		activeChannel?.token,
		activeChannel?.id,
		activeChannel?.livekitUrl,
		handleLeaveChannel,
		setRoom,
		refreshActiveChannelToken,
	]);

	// Clean up orphaned voiceChannel tabs once no call and no broadcast is left.
	useEffect(() => {
		if (!(activeChannel || broadcast)) {
			const hasVoiceTab = gridItems.some((item) =>
				item.tabs.some((tab) => tab.type === "voiceChannel")
			);
			if (hasVoiceTab) {
				uiStore.setState((state) => {
					for (const item of state.gridItems) {
						const voiceTabIndex = item.tabs.findIndex(
							(tab) => tab.type === "voiceChannel"
						);
						if (voiceTabIndex !== -1) {
							item.tabs.splice(voiceTabIndex, 1);
							if (item.activeTabIndex >= item.tabs.length) {
								item.activeTabIndex = Math.max(0, item.tabs.length - 1);
							}
						}
					}
					state.gridItems = state.gridItems.filter(
						(item) => item.tabs.length > 0
					);
				});
			}
		}
	}, [activeChannel, broadcast, gridItems]);

	useEffect(() => {
		if (!activeBroadcastViewer?.token) return;

		let mounted = true;
		let currentRoom: Room | null = null;
		const abortController = new AbortController();

		const connectToBroadcast = async () => {
			try {
				const newRoom = new Room({
					adaptiveStream: true,
					dynacast: true,
				});

				currentRoom = newRoom;

				if (abortController.signal.aborted) {
					newRoom.disconnect();
					return;
				}

				newRoom.on("disconnected", () => {
					console.log("Broadcast room disconnected");
				});

				await newRoom.connect(
					livekitUrlFor(activeBroadcastViewer.livekitUrl),
					activeBroadcastViewer.token
				);

				if (abortController.signal.aborted || !mounted) {
					newRoom.disconnect();
					return;
				}

				// Satisfy browser autoplay policy so broadcast audio plays immediately
				newRoom.startAudio().catch(() => {});

				broadcastTokenRefreshAttemptedRef.current = false;
				setBroadcastRoom(newRoom);
			} catch (error) {
				if (abortController.signal.aborted) return;
				const isExpiredToken =
					error instanceof Error &&
					/token is expired|expired token/i.test(error.message);
				if (
					isExpiredToken &&
					mounted &&
					!broadcastTokenRefreshAttemptedRef.current
				) {
					broadcastTokenRefreshAttemptedRef.current = true;
					try {
						await refreshActiveBroadcastViewerToken();
						return;
					} catch (refreshError) {
						if (SENTRY_ENABLED)
							captureException(refreshError, {
								tags: { context: "grid_broadcast_token_refresh" },
							});
					}
				}
				if (SENTRY_ENABLED)
					captureException(error, {
						tags: { context: "grid_broadcast_connect" },
					});
				console.error("Failed to connect to broadcast:", error);
				if (mounted) {
					toast.error("Failed to connect to broadcast. Please try again.");
					handleLeaveBroadcast();
				}
			}
		};

		connectToBroadcast();

		return () => {
			mounted = false;
			abortController.abort();

			if (currentRoom && !activeBroadcastViewer) {
				currentRoom.disconnect();
			}
		};
	}, [
		activeBroadcastViewer?.token,
		activeBroadcastViewer?.livekitUrl,
		handleLeaveBroadcast,
		setBroadcastRoom,
		refreshActiveBroadcastViewerToken,
	]);

	useEffect(() => {
		if (!broadcastRoom) {
			setIsBroadcastAudioActive(false);
			return;
		}

		const handleActiveSpeakersChanged = (speakers: { identity: string }[]) => {
			setIsBroadcastAudioActive(speakers.length > 0);
		};

		broadcastRoom.on("activeSpeakersChanged", handleActiveSpeakersChanged);

		return () => {
			broadcastRoom.off("activeSpeakersChanged", handleActiveSpeakersChanged);
			setIsBroadcastAudioActive(false);
		};
	}, [broadcastRoom, setIsBroadcastAudioActive]);

	// No `!isConnected` full-screen overlay here. The /app route owns
	// connection-status UI (D2 / TECH-2944). On a cold miss it shows the
	// full-screen spinner, and with the client-side cache warm it paints the
	// cached view read-only behind a small top-center "Connecting…" pill, which
	// also carries the reconnect countdown. A blocking overlay here would cover
	// that view and double up the indicator.
	return (
		<TooltipProvider>
			<div className="flex h-full overflow-hidden">
				<SidebarProvider>
					{/* TODO: Re-enable if mobile Sheet sidebar is restored -  md:hidden */}
					<SidebarTrigger className="fixed top-2 left-2 z-50 hidden" />
					<Sidebar />
					<SidebarInset>
						<Card
							className="h-full flex-1 overflow-hidden rounded-none p-0 md:mt-2 md:mb-2 md:rounded-md"
							data-tour="main-grid"
						>
							<div className="flex h-full flex-1 flex-col overflow-hidden">
								{gridItems.length === 0 ? (
									<div className="flex h-full items-center justify-center">
										<div className="text-center">
											<h3 className="font-semibold text-lg">
												No items on grid
											</h3>
											<p className="text-muted-foreground text-sm">
												Click on a channel or thread from the sidebar to add it
												to the workspace
											</p>
										</div>
									</div>
								) : (
									<div className="relative h-full w-full flex-1">
										{gridItems.map((item) => {
											const activeTab = item.tabs[item.activeTabIndex];
											if (!activeTab) return null;

											const route = gridRoutes[activeTab.type];
											if (!route) {
												console.error(
													`Unknown grid route type: "${activeTab.type}"`
												);
												return null;
											}
											const Item = route.component as React.ComponentType<any>;

											const staticTabInfo: Record<
												string,
												{ label: string; icon: typeof Hash }
											> = {
												schedule: {
													label: scheduleSettings.scheduleTitle || "Schedule",
													icon: CalendarDays,
												},
												broadcastControls: {
													label: "Broadcast Controls",
													icon: Radio,
												},
											};

											const getTabInfo = (tab: typeof activeTab) => {
												if (
													tab.type === "textChannel" &&
													tab.params &&
													"channelId" in tab.params
												) {
													const channel = channels.get(tab.params.channelId);
													return {
														label: channel?.name || "Channel",
														icon: Hash,
													};
												}
												if (tab.type === "voiceChannel") {
													let label = "Broadcast";
													if (activeChannel && broadcast) label = "Video";
													else if (activeChannel) label = activeChannel.name;
													return { label, icon: Video };
												}
												if (
													tab.type === "thread" &&
													tab.params &&
													"threadId" in tab.params
												) {
													const channel = channels.get(tab.params.threadId);
													return {
														label: channel?.name || "Thread",
														icon: MessageSquare,
													};
												}
												if (
													tab.type === "document" &&
													tab.params &&
													"documentId" in tab.params
												) {
													const doc = documents.get(tab.params.documentId);
													return {
														label: doc?.name || "Document",
														icon: FileText,
													};
												}
												return (
													staticTabInfo[tab.type] ?? {
														label: "Tab",
														icon: Hash,
													}
												);
											};

											return (
												<div
													className="absolute flex flex-col"
													key={item.id}
													style={{
														left: `${item.x * 100}%`,
														top: `${item.y * 100}%`,
														width: `${item.w * 100}%`,
														height: `${item.h * 100}%`,
													}}
												>
													{item.tabs.length > 1 && (
														<div
															className="scrollbar-hide flex overflow-x-auto border-b bg-background"
															data-tour="tabs-click"
														>
															{item.tabs.map((tab, index) => {
																const tabKey = tab.params
																	? `${tab.type}-${JSON.stringify(tab.params)}`
																	: `${tab.type}-${index}`;
																const isActive = index === item.activeTabIndex;
																return (
																	<button
																		className={`group flex items-center gap-2 border-r px-3 py-2 text-sm transition-colors hover:bg-accent ${
																			isActive
																				? "border-b-2 border-b-primary bg-accent"
																				: ""
																		}`}
																		key={tabKey}
																		onClick={() => setActiveTab(item.id, index)}
																		ref={(el) => {
																			if (el && isActive) {
																				el.scrollIntoView({
																					behavior: "smooth",
																					block: "nearest",
																					inline: "center",
																				});
																			}
																		}}
																		type="button"
																	>
																		{(() => {
																			const { label, icon: Icon } =
																				getTabInfo(tab);
																			return (
																				<>
																					<Icon className="size-3" />
																					<span className="max-w-[150px] truncate">
																						{label}
																					</span>
																				</>
																			);
																		})()}
																		<div
																			className={cn(
																				buttonVariants({
																					variant: "ghost",
																					size: "icon",
																				}),
																				"size-4 p-0 opacity-0 transition-opacity group-hover:opacity-100"
																			)}
																			onClick={(e) => {
																				e.stopPropagation();
																				closeTab(item.id, index);
																			}}
																			onKeyDown={(e) => {
																				if (
																					e.key === "Enter" ||
																					e.key === " "
																				) {
																					e.stopPropagation();
																					closeTab(item.id, index);
																				}
																			}}
																			role="button"
																			tabIndex={0}
																		>
																			<X className="size-3" />
																		</div>
																	</button>
																);
															})}
														</div>
													)}
													{/* Tab content */}
													<div className="flex-1 overflow-hidden">
														<Item {...activeTab.params} />
													</div>
												</div>
											);
										})}
									</div>
								)}
							</div>
						</Card>
					</SidebarInset>
					<RightSidebar />
				</SidebarProvider>
				{activeChannel && room && (
					<DndContext modifiers={[restrictToWindowEdges]}>
						<RoomContext.Provider value={room}>
							<RoomAudioRenderer />
						</RoomContext.Provider>
					</DndContext>
				)}
				{broadcastRoom && (
					<DndContext modifiers={[restrictToWindowEdges]}>
						<RoomContext.Provider value={broadcastRoom}>
							<RoomAudioRenderer />
						</RoomContext.Provider>
					</DndContext>
				)}
				<FloatingPipPanel />
				<AppTour />
			</div>
		</TooltipProvider>
	);
}

export function GridLink({
	to,
	children,
	onClick,
	...props
}: {
	to: GridRoutes;
} & React.HTMLAttributes<HTMLSpanElement>) {
	const navigate = useStore(uiStore, (state) => state.navigate);
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: fine if ignored
		// biome-ignore lint/a11y/noNoninteractiveElementInteractions: fine if ignored
		// biome-ignore lint/a11y/useKeyWithClickEvents: fine if ignored
		<span
			{...props}
			onClick={(e) => {
				e.preventDefault();
				navigate(to);
				onClick?.(e);
			}}
		>
			{children}
		</span>
	);
}
